/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

// React
import React from 'react';
// redux hooks
import { useFieldArray, Controller } from 'react-hook-form';
// BASE components
import BASESvgIcon from '../../../../../../../@BASE/core/BASESvgIcon';
import BASEUtils from '../../../../../../../@BASE/utils/BASEUtils';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionActions from '@mui/material/AccordionActions';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from '@mui/system/Box';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from '@mui/material/Typography';
// custom components
import NumberedHeading from '../../../../../../shared-components/forms/NumberedHeading';
import ImageAttribution from '../../shared/ImageAttribution';
import WYSIWYGEditor from '../../../../../../shared-components/WYSIWYGEditor';
// data
import { ReviewData, MediaObject } from '../../../data/Defaults';

export default function MissionSelectionArray({
  control,
  register,
  setValue,
  getValues,
  errors,
  inputDir,
  possiblePages
}) {
  // setup field array
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'content.core'
  });
  if (possiblePages === null) {
    return;
  }

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0
          }
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" sx={{ marginTop: '22px' }} />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="py-0">
                <Accordion
                  defaultExpanded
                  className="bg-transparent shadow-none"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{
                      border: 'solid 1px',
                      borderColor: '#bdbdbd'
                    }}
                  >
                    <Typography
                      className="grow m-0 font-semibold"
                      sx={{ lineHeight: '28px' }}
                    >
                      Option {index + 1}
                    </Typography>
                    {index !== 0 && (
                      <IconButton
                        className="ml-16 mr-8"
                        aria-label="move up"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index - 1);
                        }}
                      >
                        <BASESvgIcon size={20}>
                          heroicons-solid:arrow-circle-up
                        </BASESvgIcon>
                      </IconButton>
                    )}
                    {index !== fields.length - 1 && (
                      <IconButton
                        className="mx-8"
                        aria-label="move down"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          move(index, index + 1);
                        }}
                      >
                        <BASESvgIcon size={20}>
                          heroicons-solid:arrow-circle-down
                        </BASESvgIcon>
                      </IconButton>
                    )}
                    <IconButton
                      className="ml-8 mr-16"
                      aria-label="delete"
                      color="error"
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        remove(index);
                      }}
                    >
                      <BASESvgIcon size={20}>
                        heroicons-outline:trash
                      </BASESvgIcon>
                    </IconButton>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box className="p-0">
                      <Controller
                        control={control}
                        name={`content.core.${index}.id`}
                        render={({ field }) => (
                          <input type="hidden" {...field} />
                        )}
                      />

                      <Controller
                        control={control}
                        name={`content.core.${index}.review.status`}
                        render={({ field }) => (
                          <input type="hidden" {...field} />
                        )}
                      />

                      <Controller
                        control={control}
                        name={`content.core.${index}.review.approved`}
                        render={({ field }) => (
                          <input type="hidden" {...field} />
                        )}
                      />

                      <Controller
                        control={control}
                        name={`content.core.${index}.review.approval_date`}
                        render={({ field }) => (
                          <input type="hidden" {...field} />
                        )}
                      />

                      <NumberedHeading
                        number="1"
                        title="Image"
                        instructions="Add Image Attribution."
                        required
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.media`}
                        render={({ field }) => (
                          <ImageAttribution
                            targetObj={`content.core.${index}.media`}
                            errors={errors.content?.core?.[index]?.media}
                            inputDir={inputDir}
                            {...{ control }}
                          />
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="2"
                        title="TL Title"
                        instructions={`Title of option ${index + 1} in the TL.`}
                        required
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.title_tl`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="TL Title"
                              placeholder="TL Title"
                              error={!!errors.content?.core?.[index]?.title_tl}
                              helperText={
                                errors.content?.core?.[index]?.title_tl?.message
                              }
                              fullWidth
                              required
                              dir={inputDir}
                              size="small"
                            />
                          </>
                        )}
                      />

                      <NumberedHeading
                        number="3"
                        title="TL Description"
                        instructions={`Text that will appear on-screen describing option ${
                          index + 1
                        } in the TL.`}
                        required
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.text_tl`}
                        render={({ field }) => (
                          <WYSIWYGEditor
                            {...field}
                            error={errors.content?.core?.[index]?.text_tl}
                          />
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="4"
                        title="English Title"
                        instructions={`Title of option ${
                          index + 1
                        } in English.`}
                        required
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.title_en`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="English Title"
                              placeholder="English Title"
                              error={!!errors.content?.core?.[index]?.title_en}
                              helperText={
                                errors.content?.core?.[index]?.title_en?.message
                              }
                              fullWidth
                              required
                              size="small"
                            />
                          </>
                        )}
                      />

                      <NumberedHeading
                        number="5"
                        title="English Description"
                        instructions={`Text that will appear on-screen describing option ${
                          index + 1
                        } in English.`}
                        required
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.text_en`}
                        render={({ field }) => (
                          <WYSIWYGEditor
                            {...field}
                            error={errors.content?.core?.[index]?.text_en}
                          />
                        )}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading
                        number="6"
                        title="Progression ID"
                        instructions={`This is the resulting page for selecting option ${
                          index + 1
                        }.`}
                      />
                      <Controller
                        control={control}
                        name={`content.core.${index}.progression_id`}
                        render={({ field }) => (
                          <>
                            <FormControl
                              className="mt-16"
                              fullWidth
                              size="small"
                              error={
                                !!errors.content?.core?.[index]?.progression_id
                              }
                            >
                              <InputLabel
                                id={`progression-page-select-label-${index}`}
                              >
                                Progression Page
                              </InputLabel>
                              <Select
                                labelId={`progression-page-select-label-${index}`}
                                id={`progression-page-select-${index}`}
                                {...field}
                                label="progression Page"
                                value={
                                  possiblePages.find(
                                    (page) => page.id === field.value
                                  )
                                    ? field.value
                                    : ''
                                }
                              >
                                <MenuItem value="" disabled>
                                  <em>Select a progression page</em>
                                </MenuItem>
                                {possiblePages.map((page) => (
                                  <MenuItem key={page.id} value={page.id}>
                                    {page.label} - {page.title}
                                  </MenuItem>
                                ))}
                              </Select>
                              <FormHelperText>
                                {
                                  errors.content?.core?.[index]?.progression_id
                                    ?.message
                                }
                              </FormHelperText>
                            </FormControl>
                          </>
                        )}
                      />
                    </Box>
                  </AccordionDetails>
                  <AccordionActions>
                    {index !== 0 && (
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index - 1)}
                        startIcon={
                          <BASESvgIcon size={20}>
                            heroicons-solid:arrow-circle-up
                          </BASESvgIcon>
                        }
                        size="small"
                      >
                        Move Up
                      </Button>
                    )}
                    {index !== fields.length - 1 && (
                      <Button
                        variant="outlined"
                        onClick={() => move(index, index + 1)}
                        startIcon={
                          <BASESvgIcon size={20}>
                            heroicons-solid:arrow-circle-down
                          </BASESvgIcon>
                        }
                        size="small"
                      >
                        Move Down
                      </Button>
                    )}
                    <Button
                      variant="contained"
                      onClick={() => remove(index)}
                      color="error"
                      startIcon={
                        <BASESvgIcon size={20}>
                          heroicons-outline:trash
                        </BASESvgIcon>
                      }
                      size="small"
                    >
                      Remove
                    </Button>
                  </AccordionActions>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-8 rounded cursor-pointer"
          color="secondary"
          variant="contained"
          onClick={() =>
            append({
              id: BASEUtils.generateGUID(),
              review: ReviewData,
              media: MediaObject,
              title_tl: '',
              text_tl: '',
              title_en: '',
              text_en: '',
              progression_id: ''
            })
          }
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add Option
          </span>
        </Button>
      </Box>
    </>
  );
}
